import React from "react";
import "./Header.css";
import { Box, Dropdown, Link, Menu, MenuButton, MenuItem, IconButton } from "@mui/joy";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";

const navLinks = [
  { label: "Federal Bills", path: "/bills" },
  { label: "State Bills", path: "/state-bills" },
  { label: "Legislators", path: "/legislators" },
  { label: "Resources", path: "/resources" },
];

const Header = () => {
  const [currentPath, setCurrentPath] = React.useState(window.location.pathname);

  React.useEffect(() => {
    const onPopState = () => setCurrentPath(window.location.pathname);
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const navigate = (path) => {
    window.history.pushState({}, "", path);
    const navEvent = new PopStateEvent("popstate");
    window.dispatchEvent(navEvent);
  };

  const handleNavigation = (event, path) => {
    event.preventDefault();
    navigate(path);
  };

  const handleKeyDown = (event, path) => {
    if (event.key === "Enter") {
      event.preventDefault();
      navigate(path);
    }
  };

  const isActive = (path) => {
    if (path === "/") return currentPath === "/";
    return currentPath.startsWith(path);
  };

  return (
    <Box
      component="header"
      role="banner"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 1000,
        backgroundColor: "var(--ce-header-bg)",
        borderBottom: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: { xs: "0 1.25rem", md: "0 2rem" },
          height: { xs: "56px", md: "60px" },
          maxWidth: "1440px",
          margin: "0 auto",
          width: "100%",
        }}
      >
        <Link
          href="/"
          underline="none"
          onClick={(e) => handleNavigation(e, "/")}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
          aria-label="CivicEngage Home"
        >
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "8px",
              backgroundColor: "var(--ce-accent)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <AccountBalanceIcon sx={{ color: "white", fontSize: 20 }} />
          </Box>
          <Box
            sx={{
              fontSize: { xs: "18px", md: "20px" },
              fontWeight: 700,
              letterSpacing: "-0.01em",
              color: "var(--ce-header-text)",
            }}
          >
            CivicEngage
          </Box>
        </Link>

        <nav aria-label="Main navigation">
          <Box sx={{ display: { xs: "inline-flex", sm: "none" } }}>
            <Dropdown>
              <MenuButton
                slots={{ root: IconButton }}
                slotProps={{
                  root: {
                    variant: "plain",
                    sx: {
                      color: "var(--ce-header-text)",
                      borderRadius: "6px",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.08)",
                      },
                    },
                  },
                }}
                aria-label="Open navigation menu"
              >
                <MenuRoundedIcon />
              </MenuButton>
              <Menu
                sx={{
                  minWidth: 200,
                  boxShadow: "0 8px 24px oklch(10% 0.05 255 / 0.3)",
                  borderRadius: "8px",
                  padding: "6px",
                  backgroundColor: "var(--ce-header-bg)",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              >
                {navLinks.map((link) => (
                  <MenuItem
                    key={link.path}
                    onClick={(e) => handleNavigation(e, link.path)}
                    sx={{
                      borderRadius: "6px",
                      padding: "10px 14px",
                      fontSize: "15px",
                      fontWeight: isActive(link.path) ? 600 : 400,
                      color: isActive(link.path) ? "var(--ce-accent)" : "var(--ce-header-text)",
                      backgroundColor: isActive(link.path)
                        ? "rgba(0, 82, 163, 0.25)"
                        : "transparent",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.08)",
                        color: "var(--ce-header-text)",
                      },
                    }}
                  >
                    {link.label}
                  </MenuItem>
                ))}
              </Menu>
            </Dropdown>
          </Box>

          <Box
            component="ul"
            role="list"
            sx={{
              display: { xs: "none", sm: "flex" },
              gap: 0.5,
              margin: 0,
              padding: 0,
              listStyle: "none",
              alignItems: "center",
            }}
          >
            {navLinks.map((link) => (
              <Box component="li" key={link.path}>
                <Link
                  href={link.path}
                  underline="none"
                  onClick={(e) => handleNavigation(e, link.path)}
                  onKeyDown={(e) => handleKeyDown(e, link.path)}
                  aria-current={isActive(link.path) ? "page" : undefined}
                  sx={{
                    display: "block",
                    padding: "8px 14px",
                    borderRadius: "6px",
                    fontSize: "14px",
                    fontWeight: isActive(link.path) ? 600 : 500,
                    color: isActive(link.path)
                      ? "#95B9D0"
                      : "rgba(232, 230, 227, 1)",
                    backgroundColor: isActive(link.path)
                      ? "rgba(0, 82, 163, 0.25)"
                      : "transparent",
                    transition: "background-color 0.15s ease, color 0.15s ease",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.08)",
                      color: "var(--ce-header-text)",
                    },
                    "@media (prefers-reduced-motion: reduce)": {
                      transition: "none",
                    },
                  }}
                >
                  {link.label}
                </Link>
              </Box>
            ))}
          </Box>
        </nav>
      </Box>
    </Box>
  );
};

export default Header;
